/**
 * Segment dashboard screen — exact parity with frontend's SegmentDashboards.
 * SegmentHeader → Overview (PageBuilder) | Synthetic Audience (digital twin chat)
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Container } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { SegmentHeader } from '@trinity/chat-components/components/SegmentHeader/index.js';
import type { SegmentHeaderTab } from '@trinity/chat-components/components/SegmentHeader/index.js';
import {
  SegmentPageBuilderContainer,
  SegmentPageBuilderApiProvider,
} from '@trinity/chat-components/containers/SegmentPageBuilder/index.js';
import type { SegmentPageBuilderApi } from '@trinity/chat-components/containers/SegmentPageBuilder/index.js';
import { DigitalTwinChat } from '@trinity/chat-components/components/DigitalTwin/index.js';
import { PageRenderer } from '@trinity/chat-components/components/PageDashboard/PageRenderer/index.js';
import chatBackgroundImg from '@trinity/chat-components/static/images/chat-background.png';
import { getAuthenticatedClient } from '@/api/client';
import { ChatInterfaceNative } from '@/screens/Segmentation/components/ChatInterfaceNative';
import SegmentMarketViewPanel from '@/screens/Segmentation/components/SegmentMarketViewPanel';
import SegmentOverviewCard from '@/screens/Segmentation/components/SegmentOverviewCard';

function client() { return getAuthenticatedClient(); }

interface TtsSettings {
  avatarId?: string;
  ttsConfig?: Record<string, unknown> | null;
}

interface SegmentData {
  id: number;
  identifier: string;
  projectId: number;
  name: string;
  description: string | null;
  metadata: {
    subtitle?: string;
    attributes?: string[];
    sampleData?: { segmentN: number; totalN: number };
    marketView?: Record<string, unknown>;
    segmentProfile?: Record<string, unknown>;
  };
  dashboardPageId: number | null;
  iconUrl: string | null;
  isPublished: boolean;
  ttsSettings?: TtsSettings | null;
}

type TabKey = 'overview' | 'digital-twin';

const TAB_ROUTES: { key: TabKey; title: string }[] = [
  { key: 'overview', title: 'Overview' },
  { key: 'digital-twin', title: 'Digital Twin' },
];

const TABS: SegmentHeaderTab[] = TAB_ROUTES.map(r => ({ key: r.key, title: r.title }));

export type SegmentDashboardScreenProps = {
  /** Override for the breadcrumb back link. Defaults to '/segments'. */
  backTo?: string;
  /** Override for the breadcrumb label. Defaults to 'Segments'. */
  backLabel?: string;
};

export function SegmentDashboardScreen({
  backTo = '/segments',
  backLabel = 'Segments',
}: SegmentDashboardScreenProps = {}): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const segmentId = Number(id);

  const [segment, setSegment] = useState<SegmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (isNaN(segmentId)) return;
    client().get(`/segments/${segmentId}`)
      .then((res) => setSegment(res.data.segment))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [segmentId]);

  const api = useMemo((): SegmentPageBuilderApi => ({
    getSegmentPage: async () => {
      const res = await client().get(`/segments/${segmentId}/page`);
      const page = res.data;
      // Management API returns config inside currentVersion, not top-level
      return {
        id: page.id,
        name: page.name,
        config: page.currentVersion?.config ?? page.config ?? {},
      };
    },
    savePageVersion: async (_pId, _sId, data) => {
      const res = await client().post(`/segments/${segmentId}/page/version`, data);
      return res.data;
    },
    revertPageVersion: async (_pId, _sId, versionId) => {
      const res = await client().post(`/segments/${segmentId}/page/revert/${versionId}`);
      return res.data;
    },
  }), [segmentId]);

  // Chat page context omitted until projects are integrated in genai.
  // Digital Twin works without segment-specific scoping.
  const chatPageContext = undefined;

  const handleTabChange = useCallback((index: number) => setActiveTab(index), []);
  const handleChatWithSegment = useCallback(() => setActiveTab(1), []);
  const pageAreaRef = useRef<HTMLDivElement>(null);

  // Force-collapse the "Segment Model Results" block on mount
  useEffect(() => {
    const container = pageAreaRef.current;
    if (!container) return;
    const collapseModelResults = (): void => {
      const allTitles = container.querySelectorAll<HTMLElement>('.MuiTypography-root');
      for (const t of Array.from(allTitles)) {
        if ((t.textContent || '').trim() === 'Segment Model Results' || (t.textContent || '').trim() === 'Segmentation Model Results') {
          const accordion = t.closest<HTMLElement>('.MuiAccordion-root');
          if (accordion) {
            const summary = accordion.querySelector<HTMLElement>('.MuiAccordionSummary-root');
            if (summary?.getAttribute('aria-expanded') === 'true') {
              summary.click();
            }
          }
          break;
        }
      }
    };
    const t = setTimeout(collapseModelResults, 500);
    return () => clearTimeout(t);
  }, [segment]);

  if (loading) return <Root><Wrap maxWidth="xl"><LoadingText>Loading...</LoadingText></Wrap></Root>;
  if (!segment) return <Root><Wrap maxWidth="xl"><LoadingText>Segment not found</LoadingText></Wrap></Root>;

  return (
    <Root $chatOpen={chatOpen}>
      <LeftModule>
      <Wrap maxWidth="xl">
        {/* Breadcrumbs — matches frontend Breadcrumbs component exactly */}
        <BreadcrumbsRow>
          <BreadcrumbCrumb onClick={() => navigate('/home')}>Home</BreadcrumbCrumb>
          <ChevronRightIcon />
          <BreadcrumbCrumb onClick={() => navigate(backTo)}>{backLabel}</BreadcrumbCrumb>
          <ChevronRightIcon />
          <BreadcrumbCrumb $current>{segment.name}</BreadcrumbCrumb>
        </BreadcrumbsRow>

        {/* Segment Header — same shared component as frontend */}
        <SegmentHeader
          name={segment.name}
          subtitle={segment.metadata.subtitle}
          attributes={segment.metadata.attributes}
          iconUrl={segment.iconUrl ?? undefined}
          sampleData={segment.metadata.sampleData}
          tabs={TABS}
          activeTabIndex={activeTab}
          onTabChange={handleTabChange}
          onChatWithSegment={handleChatWithSegment}
        />

        {/* Overview tab */}
        {TAB_ROUTES[activeTab]?.key === 'overview' && (
          <>
            {segment.metadata.marketView ? (
              <>
                <SegmentOverviewCard
                  narrative={(segment.metadata.segmentProfile as Record<string, unknown> | undefined)?.profile_narrative as string | undefined}
                  commercialAction={(segment.metadata.segmentProfile as Record<string, unknown> | undefined)?.commercial_action as string | undefined}
                  gapAnalysis={(segment.metadata.segmentProfile as Record<string, unknown> | undefined)?.gap_analysis as string | Record<string, unknown> | undefined}
                />
                <div style={{ height: 16 }} />
                <SegmentMarketViewPanel
                  data={segment.metadata.marketView}
                  segmentName={segment.name}
                />
              </>
            ) : null}
            {segment.dashboardPageId ? (
              segment.metadata.marketView ? (
                <div ref={pageAreaRef}>
                  <PageRenderer pageId={segment.dashboardPageId} />
                </div>
              ) : (
                <SegmentPageBuilderApiProvider value={api}>
                  <SegmentPageBuilderContainer
                    projectId={segment.projectId}
                    segmentId={segment.id}
                  />
                </SegmentPageBuilderApiProvider>
              )
            ) : (
              <EmptyCard>No dashboard configured for this segment.</EmptyCard>
            )}
          </>
        )}

        {/* Digital Twin tab */}
        {TAB_ROUTES[activeTab]?.key === 'digital-twin' && (
          <DigitalTwinChat
            avatarId={segment.ttsSettings?.avatarId ?? undefined}
            chatPageContext={chatPageContext as never}
            ttsConfig={(segment.ttsSettings?.ttsConfig ?? null) as never}
            disableVideoMode
            segmentIconUrl={segment.iconUrl ?? undefined}
          />
        )}

      </Wrap>
      </LeftModule>

      {chatOpen ? (
        <RightModule>
          <ChatInterfaceNative segmentIdentifier={segment?.identifier} />
        </RightModule>
      ) : (
        <ChatToggleBtn onClick={() => setChatOpen(true)} title="Chat with Segment">
          <AutoAwesomeIcon sx={{ fontSize: 24 }} />
        </ChatToggleBtn>
      )}
    </Root>
  );
}

// ─── Styled — matches frontend SegmentDashboards exactly ────────────────────

const Root = styled.div<{ $chatOpen?: boolean }>`
  display: flex;
  min-height: calc(100vh - var(--navbar-height, 60px));
  background: url(${chatBackgroundImg}) no-repeat;
  background-size: cover;
  padding-bottom: 48px;
  gap: 0;
`;

const LeftModule = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const RightModule = styled.div`
  width: 420px;
  flex-shrink: 0;
  border-left: 1px solid var(--sec-01, #e5e5e5);
  background: var(--pri-01, #ffffff);
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--navbar-height, 60px));
  position: sticky;
  top: 0;
`;

const ChatToggleBtn = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--pri-02, #28AAE1);
  color: #ffffff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(40, 170, 225, 0.4);
  transition: transform 0.2s;
  z-index: 10;
  &:hover { transform: scale(1.1); }
`;

const Wrap = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  margin: '0 auto',
});

const BreadcrumbsRow = styled.div`
  display: flex; align-items: center; flex-wrap: wrap;
  padding: 8px 0;
`;

const ChevronRightIcon = styled(ChevronRight)`
  margin: 0 5px;
  color: var(--gray-d, #626262);
  font-size: 20px;
`;

const BreadcrumbCrumb = styled.div<{ $current?: boolean }>`
  display: flex; align-items: center;
  cursor: pointer;
  font-family: var(--font-semibold, inherit);
  font-size: 14px;
  padding: 8px;
  color: var(--gray-d, #626262);
  border-radius: 8px;
  &:hover { background-color: var(--gray-xl, #fafafa); }
  ${(p) => p.$current && `cursor: default; &:hover { background: none; }`}
`;

const LoadingText = styled.div`
  display: flex; align-items: center; justify-content: center;
  min-height: 300px; color: #adadad; font-size: 14px;
`;

const EmptyCard = styled.div`
  background: #fff; border: 1px solid var(--sec-01, #e5e5e5); border-radius: 5px;
  padding: 64px; text-align: center; color: #626262; font-size: 14px;
`;

